<template>
  <div class="em-chart-card">
    <div class="em-chart-card__header">
      <div class="em-chart-card__icon em-chart-card__icon--slate">
        <font-awesome-icon :icon="['fas', 'clipboard-list']" />
      </div>
      <div>
        <h3 class="em-chart-card__title">Employer List Coverage</h3>
        <p class="em-chart-card__subtitle">Click a segment to view employers</p>
      </div>
    </div>

    <div v-if="hasData" class="em-chart-card__body em-chart-card__body--center">
      <apexchart
        type="donut"
        height="320"
        :options="chartOptions"
        :series="chartSeries"
        @dataPointSelection="onSegmentSelect"
      />
    </div>
    <div v-else class="em-chart-empty">
      <p class="em-chart-empty__text">No coverage data available</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EmployerListCoverageChart',
  props: {
    data: {
      type: Array,
      default: function () {
        return [];
      },
    },
  },
  emits: ['segment-clicked'],
  computed: {
    hasData: function () {
      if (!this.data || this.data.length === 0) {
        return false;
      }
      var total = 0;
      for (var i = 0; i < this.data.length; i++) {
        total += Number(this.data[i].count || 0);
      }
      return total > 0;
    },
    chartSeries: function () {
      return (this.data || []).map(function (item) {
        return Number(item.count || 0);
      });
    },
    chartOptions: function () {
      var labels = (this.data || []).map(function (item) {
        return item.label;
      });
      return {
        chart: {
          type: 'donut',
          toolbar: { show: false },
          fontFamily: 'inherit',
        },
        labels: labels,
        colors: ['#059669', '#94a3b8'],
        legend: {
          position: 'bottom',
          fontSize: '12px',
          markers: { width: 10, height: 10, radius: 10 },
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px',
            fontWeight: 700,
          },
          dropShadow: { enabled: false },
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: '62%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#64748b',
                },
                value: {
                  show: true,
                  fontSize: '22px',
                  fontWeight: 800,
                  color: '#0f172a',
                },
                total: {
                  show: true,
                  label: 'Employers',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#64748b',
                  formatter: function (w) {
                    var sum = 0;
                    var series = w.globals.seriesTotals || [];
                    for (var i = 0; i < series.length; i++) {
                      sum += series[i];
                    }
                    return sum;
                  },
                },
              },
            },
          },
        },
        stroke: {
          width: 2,
          colors: ['#ffffff'],
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + (val === 1 ? ' employer' : ' employers');
            },
          },
        },
      };
    },
  },
  methods: {
    resolveInCatalog: function (label, index) {
      var text = String(label || '').toLowerCase();
      if (text.indexOf('not') !== -1) {
        return false;
      }
      if (text.indexOf('in employer list') !== -1 || text.indexOf('in list') !== -1) {
        return true;
      }
      // Fallback to known coverage order: [In list, Not in list]
      return index === 0;
    },
    onSegmentSelect: function (event, chartContext, config) {
      var idx = config && config.dataPointIndex != null ? config.dataPointIndex : -1;
      if (idx < 0 || !this.data || !this.data[idx]) {
        return;
      }
      var item = this.data[idx];
      if (!item || !Number(item.count || 0)) {
        return;
      }
      this.$emit('segment-clicked', {
        label: item.label || '',
        in_employers_catalog: this.resolveInCatalog(item.label, idx),
        count: Number(item.count || 0),
      });
    },
  },
};
</script>

<style scoped>
.em-chart-card {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1.25rem;
  height: 100%;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.em-chart-card__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.em-chart-card__icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.em-chart-card__icon--slate {
  background: #e2e8f0;
  color: #334155;
}
.em-chart-card__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}
.em-chart-card__subtitle {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
}
.em-chart-card__body {
  min-height: 320px;
}
.em-chart-card__body--center {
  display: flex;
  align-items: center;
  justify-content: center;
}
.em-chart-card__body--center :deep(.apexcharts-pie-area) {
  cursor: pointer;
}
.em-chart-empty {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #cbd5e1;
  border-radius: 0.75rem;
  background: #f8fafc;
}
.em-chart-empty__text {
  margin: 0;
  color: #64748b;
  font-size: 0.875rem;
}
</style>
